import { ChangeEvent, useCallback, useMemo, useState, WheelEvent } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "react-tooltip";
import { FieldArray, useFormikContext } from "formik";
import styled from "styled-components";

import { Button, ButtonTheme } from "@components/Button";
import { ErrorRenderer, FormError } from "@components/FormError";
import { Icon } from "@components/Icon";
import { Input } from "@components/Input";
import { Label } from "@components/Label";
import { MultiSelect } from "@components/MultiSelect";
import { AssetFamily, ASSETS_FAMILY, Nullable, OptionalNotNull } from "@utils";
import { MAX_DEPOSIT_VALUE, MIN_DEPOSIT_VALUE } from "./consts";
import { TCreateAuctionFormValues } from "./CreateAuctionForm";
import { device } from "@styles/breakpoints";

const Container = styled.div`
  width: 100%;
`;

const ErrorWrapper = styled.div`
  margin-top: 8px;
  margin-bottom: ${({ theme }) => theme.tripleMargin};
`;

const InlineError = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 8px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: -2px;
`;

const MultiSelectContainer = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
`;

const Title = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 12rem 12rem;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.tripleMargin};

  @media ${device.tablet} {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(10rem, 1fr) 1fr 1fr 1fr;
  gap: 20px;
  align-items: start;
  margin-bottom: ${({ theme }) => theme.tripleMargin};
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(10rem, 1fr) 1fr;
    gap: 20px;

    /* Hide buttons in main row on smaller screens */
    & > div:nth-child(3),
    & > div:nth-child(4) {
      display: none;
    }
  }

  @media (max-width: 1060px) {
    grid-template-columns: minmax(10rem, 1fr) 1fr;
    gap: 20px;

    /* Hide buttons in main row on smaller screens */
    & > div:nth-child(3),
    & > div:nth-child(4) {
      display: none;
    }
  }

  @media (max-width: 900px) {
    grid-template-columns: minmax(10rem, 1fr) 1fr;
    gap: 20px;

    /* Hide buttons in main row on smaller screens */
    & > div:nth-child(3),
    & > div:nth-child(4) {
      display: none;
    }
  }

  @media ${device.tablet} {
    grid-template-columns: 1fr; /* stack vertically on small screens */
    gap: 12px; /* a bit tighter spacing */
    /* Hide buttons in main row on smaller screens */
    & > div:nth-child(3),
    & > div:nth-child(4) {
      display: none;
    }
  }

  @media ${device.mobileL} {
    grid-template-columns: 1fr; /* stack vertically on mobile */
    gap: 8px;
    /* Hide buttons in main row on smaller screens */
    & > div:nth-child(3),
    & > div:nth-child(4) {
      display: none;
    }
  }
`;

const ButtonRow = styled.div`
  display: none;

  @media (max-width: 1200px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.tripleMargin};
    width: 100%;
  }

  @media (max-width: 1060px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.tripleMargin};
    width: 100%;
  }

  @media (max-width: 900px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.tripleMargin};
    width: 100%;
  }

  @media ${device.tablet} {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.tripleMargin};
    width: 100%;
  }
`;

const StyledInput = styled(Input)`
  width: 100%;
  box-sizing: border-box;
  height: 46px;
`;

const LabelBox = styled.div`
  display: flex;
  align-items: center;
  padding-right: 15px;

  & ${Label} {
    margin-right: auto;
  }
`;

const TooltipContent = styled.div`
  max-width: 20rem;
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;

  & button {
    width: 100%;
    height: 40px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const TitleButtonContainer = styled.div`
  @media ${device.tablet} {
    width: 100%;

    & button {
      width: 100%;
      height: 40px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

const EMPTY_STATE = {
  assets: [] as AssetFamily[],
  deposit: undefined as OptionalNotNull<number>,
};

export const DepositByAssetField = () => {
  const { t } = useTranslation();
  const {
    values: { deposits, currency },
    setFieldValue,
  } = useFormikContext<TCreateAuctionFormValues>();

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState<Nullable<typeof EMPTY_STATE>>(null);

  // Error validation logic
  const hasMaxDepositValue: boolean = deposits.some(
    ({ deposit }) => deposit && deposit > MAX_DEPOSIT_VALUE
  );
  const hasMinDepositValue: boolean = deposits.some(
    ({ deposit }) => deposit && deposit < MIN_DEPOSIT_VALUE
  );

  const assetsFamilyListAtIndex = useCallback(
    (index: number) =>
      ASSETS_FAMILY.filter(
        (asset: AssetFamily) =>
          !deposits.find(
            (deposit, idx) => deposit.assets.includes(asset) && index !== idx
          )
      ),
    [deposits]
  );

  const canAddDeposit = useMemo(
    () =>
      ASSETS_FAMILY.filter(
        (asset: AssetFamily) =>
          !value && !deposits.find((deposit) => deposit.assets.includes(asset))
      ).length > 0,
    [deposits, value]
  );

  const canSaveDeposit = useMemo(
    () =>
      value?.assets.length &&
      typeof value?.deposit === "number" &&
      value.deposit >= MIN_DEPOSIT_VALUE &&
      value.deposit <= MAX_DEPOSIT_VALUE,
    [value]
  );

  const handleAssetChange = useCallback((id: string) => {
    const asset = id as AssetFamily;

    setValue((currentValue) => {
      if (!currentValue) return { assets: [asset], deposit: undefined };

      let updatedAssets = [...currentValue.assets];

      if (updatedAssets.includes(asset)) {
        updatedAssets = updatedAssets.filter((a) => a !== asset);
      } else {
        updatedAssets.push(asset);
      }

      return { ...currentValue, assets: updatedAssets };
    });
  }, []);

  const handleDepositChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      let inputValue = event.currentTarget.value;

      // Remove leading zeros but preserve decimal numbers like 0.321
      if (
        inputValue.length > 1 &&
        inputValue.startsWith("0") &&
        !inputValue.includes(".")
      ) {
        inputValue = inputValue.replace(/^0+/, "") || "0";
        event.currentTarget.value = inputValue;
      }

      const deposit = inputValue === "" ? undefined : Number(inputValue);

      setValue((currentValue) => {
        if (!currentValue) return { deposit, assets: [] };
        return { ...currentValue, deposit };
      });
    },
    []
  );

  const handleSaveDeposit = useCallback(
    (index: number) => {
      const currentDeposits = deposits;
      currentDeposits[index] = value!;

      setFieldValue("deposits", currentDeposits);
      setIsEditing(false);
      setValue(null);
    },
    [value, deposits, setFieldValue]
  );

  const shouldShowSaveButton = useCallback(
    (index: number) => index === deposits.length - 1 && isEditing,
    [isEditing, deposits]
  );

  const canRemoveDeposit = useCallback(
    (index: number) => !isEditing || shouldShowSaveButton(index),
    [isEditing, shouldShowSaveButton]
  );

  return (
    <>
      <FieldArray
        name="deposits"
        render={(helpers) => (
          <>
            <Title>
              <LabelBox>
                <Label className="mr-6">
                  {currency
                    ? `${t("auctionForm.deposit")} (${currency})`
                    : t("auctionForm.deposit")}{" "}
                  &#42;
                </Label>
                <div id="deposit-fee-tooltip">
                  <Icon.InfoCircle />
                </div>
              </LabelBox>
              <TitleButtonContainer>
                <Button
                  type="button"
                  theme={ButtonTheme.Blue}
                  disabled={!canAddDeposit || isEditing}
                  onClick={() => {
                    setIsEditing(true);
                    helpers.push(EMPTY_STATE);
                  }}
                >
                  {t<string>("addDeposit")}
                </Button>
              </TitleButtonContainer>
            </Title>
            {deposits && (
              <>
                {deposits.length > 0 && (
                  <Container>
                    {deposits.map(({ deposit }, index) => {
                      const removeButton = (
                        <ButtonContainer>
                          <Button
                            type="button"
                            theme={ButtonTheme.Quaternary}
                            disabled={!canRemoveDeposit(index)}
                            onClick={() => {
                              setIsEditing(false);
                              setValue(null);
                              helpers.remove(index);
                            }}
                          >
                            {t<string>("removeDeposit")}
                          </Button>
                        </ButtonContainer>
                      );

                      const saveButton = shouldShowSaveButton(index) && (
                        <ButtonContainer>
                          <Button
                            type="button"
                            theme={ButtonTheme.Primary}
                            disabled={!canSaveDeposit}
                            onClick={() => handleSaveDeposit(index)}
                          >
                            {t<string>("saveDeposit")}
                          </Button>
                        </ButtonContainer>
                      );

                      const currentDeposit = shouldShowSaveButton(index)
                        ? value?.deposit
                        : deposits[index].deposit;
                      const hasInvalidDeposit =
                        currentDeposit !== undefined &&
                        (currentDeposit < MIN_DEPOSIT_VALUE ||
                          currentDeposit > MAX_DEPOSIT_VALUE);

                      return (
                        <div key={index}>
                          <Row>
                            <MultiSelectContainer>
                              <MultiSelect
                                selectAll
                                testId={`SelectDepositAssetFamily.${index}`}
                                disabled={!shouldShowSaveButton(index)}
                                noSelectedTitle={t("selectAssetFamily")}
                                options={assetsFamilyListAtIndex(index).map(
                                  (a) => ({
                                    id: a,
                                    title: a,
                                  })
                                )}
                                selectedIds={
                                  shouldShowSaveButton(index)
                                    ? value?.assets || []
                                    : deposits[index].assets
                                }
                                toggleOption={handleAssetChange}
                              />
                            </MultiSelectContainer>
                            <InputContainer>
                              <StyledInput
                                data-testid={`DepositValue.${index}`}
                                type="number"
                                value={
                                  shouldShowSaveButton(index)
                                    ? value?.deposit ?? ""
                                    : deposits[index].deposit
                                }
                                placeholder={
                                  currency
                                    ? `${t(
                                        "auctionForm.deposit"
                                      )} (${currency}) - Min: ${MIN_DEPOSIT_VALUE}`
                                    : `${t(
                                        "auctionForm.deposit"
                                      )} - Min: ${MIN_DEPOSIT_VALUE}`
                                }
                                disabled={!shouldShowSaveButton(index)}
                                onChange={handleDepositChange}
                                onWheel={(e: WheelEvent<HTMLInputElement>) =>
                                  e.currentTarget.blur()
                                }
                                min={MIN_DEPOSIT_VALUE}
                                max={MAX_DEPOSIT_VALUE}
                                step="0.01"
                                maxLength={15}
                              />
                              {hasInvalidDeposit && (
                                <InlineError>
                                  {currentDeposit < MIN_DEPOSIT_VALUE
                                    ? `${t(
                                        "auctionForm.errors.deposit.min"
                                      )} (Min: ${MIN_DEPOSIT_VALUE})`
                                    : `${t(
                                        "auctionForm.errors.deposit.max"
                                      )} (Max: ${MAX_DEPOSIT_VALUE})`}
                                </InlineError>
                              )}
                            </InputContainer>
                            {removeButton}
                            {saveButton}
                          </Row>
                          <ButtonRow>
                            {removeButton}
                            {saveButton}
                          </ButtonRow>
                        </div>
                      );
                    })}
                  </Container>
                )}
              </>
            )}
          </>
        )}
      />
      <FormError name="deposits" />
      {hasMinDepositValue && (
        <ErrorWrapper>
          <ErrorRenderer children="auctionForm.errors.deposit.min" />
        </ErrorWrapper>
      )}
      {hasMaxDepositValue && (
        <ErrorWrapper>
          <ErrorRenderer children="auctionForm.errors.deposit.max" />
        </ErrorWrapper>
      )}
      <Tooltip anchorId="deposit-fee-tooltip">
        <TooltipContent>{t("auctionForm.depositTooltip")}</TooltipContent>
      </Tooltip>
    </>
  );
};
